import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import '../styles/ContactForm.css';

const API_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:5000/api/boka'
    : 'https://umrah-backend.onrender.com/api/boka';

const PACKAGE_CONFIG = {
  host: {
    name: 'Höstpaket',
    dates: '23 Oktober - 30 Oktober 2026',
    standardFromPrice: 17495,
    premiumRoomPrices: {
      four: 17495,
      three: 18495,
      two: 19495,
    },
  },

  vinter: {
    name: 'Vinterpaket',
    dates: '20 December - 31 December 2026',
    standardFromPrice: 19995,
    premiumRoomPrices: {
      four: 19995,
      three: 20995,
      two: 21995,
    },
  },

  ramadan1: {
    name: 'Ramadanpaket 1',
    dates: '11 Februari - 20 Februari 2027',
    standardFromPrice: 21995,
    premiumRoomPrices: {
      four: 21995,
      three: 22995,
      two: 23995,
    },
  },

  ramadan2: {
    name: 'Ramadanpaket 2',
    dates: '27 Februari - 9 Mars 2027',
    standardFromPrice: 23995,
    premiumRoomPrices: {
      four: 23995,
      three: 24995,
      two: 25995,
    },
  },
};

const formatPrice = (price) =>
  new Intl.NumberFormat('sv-SE').format(price);

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    postalCode: '',
    city: '',
    phone: '',
    email: '',
    civilStatus: '',
    occupation: '',
    travelDate: '',
    package: '',
    roomType: '',
    q1: '',
    q2: '',
    q3: '',
    facePhoto: null,
    passportCopy: null,
    termsAccepted: false,
    privacyAccepted: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({
    type: '',
    message: '',
  });

  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const recaptchaRef = useRef();

  useEffect(() => {
    document.title = 'Boka din resa – Umrah Bröderna';

    if (feedback.message) {
      const t = setTimeout(
        () =>
          setFeedback({
            type: '',
            message: '',
          }),
        4000
      );

      return () => clearTimeout(t);
    }
  }, [feedback]);

  // =========================================================
  // VALT RESEPAKET + PRISLOGIK
  // =========================================================

  const selectedTravelPackage =
    PACKAGE_CONFIG[formData.travelDate] || null;

  const standardFromPrice =
    selectedTravelPackage?.standardFromPrice || 0;

  const premiumRoomPrices =
    selectedTravelPackage?.premiumRoomPrices || {
      four: 0,
      three: 0,
      two: 0,
    };

  // =========================================================
  // HANDLE CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Om användaren ändrar reseperiod:
    // nollställ Standard/Premium och eventuell rumstyp
    if (name === 'travelDate') {
      setFormData((prev) => ({
        ...prev,
        travelDate: value,
        package: '',
        roomType: '',
      }));

      return;
    }

    // Om användaren ändrar Standard/Premium:
    // nollställ eventuell tidigare rumstyp
    if (name === 'package') {
      setFormData((prev) => ({
        ...prev,
        package: value,
        roomType: '',
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // =========================================================
  // FILER
  // =========================================================

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.files[0],
    }));
  };

  // =========================================================
  // EMAIL VALIDATION
  // =========================================================

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(formData.email)) {
      setFeedback({
        type: 'error',
        message: 'Ogiltig e-postadress.',
      });

      return;
    }

    if (!recaptchaToken) {
      setFeedback({
        type: 'error',
        message: 'Vänligen bocka i reCAPTCHA.',
      });

      return;
    }

    if (!formData.termsAccepted || !formData.privacyAccepted) {
      setFeedback({
        type: 'error',
        message:
          'Du måste godkänna villkoren och integritetspolicyn.',
      });

      return;
    }

    if (
      formData.package === 'premium' &&
      !formData.roomType
    ) {
      setFeedback({
        type: 'error',
        message:
          'Vänligen välj en rumstyp för Premium-paketet.',
      });

      return;
    }

    setIsSubmitting(true);

    const body = new FormData();

    // Lägg in alla vanliga formulärfält
    Object.entries(formData).forEach(([key, value]) => {
      if (
        value !== null &&
        key !== 'facePhoto' &&
        key !== 'passportCopy'
      ) {
        body.append(key, value);
      }
    });

    // Lägg in filer
    if (formData.facePhoto) {
      body.append('facePhoto', formData.facePhoto);
    }

    if (formData.passportCopy) {
      body.append(
        'passportCopy',
        formData.passportCopy
      );
    }

    // reCAPTCHA
    body.append(
      'recaptchaToken',
      recaptchaToken
    );

    // Skicka med paketinformation
    if (selectedTravelPackage) {
      body.append(
        'travelPackageName',
        selectedTravelPackage.name
      );

      body.append(
        'travelPackageDates',
        selectedTravelPackage.dates
      );
    }

    // Skicka med priser
    body.append(
      'standardFromPrice',
      String(standardFromPrice)
    );

    body.append(
      'premiumFourFromPrice',
      String(premiumRoomPrices.four)
    );

    body.append(
      'premiumThreeFromPrice',
      String(premiumRoomPrices.three)
    );

    body.append(
      'premiumTwoFromPrice',
      String(premiumRoomPrices.two)
    );

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        body,
      });

      if (res.ok) {
        setFeedback({
          type: 'success',
          message: 'Meddelandet skickades!',
        });

        setFormData({
          firstName: '',
          lastName: '',
          address: '',
          postalCode: '',
          city: '',
          phone: '',
          email: '',
          civilStatus: '',
          occupation: '',
          travelDate: '',
          package: '',
          roomType: '',
          q1: '',
          q2: '',
          q3: '',
          facePhoto: null,
          passportCopy: null,
          termsAccepted: false,
          privacyAccepted: false,
        });

        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      } else {
        const err = await res
          .json()
          .catch(() => ({}));

        setFeedback({
          type: 'error',
          message:
            err?.error ||
            'Något gick fel, försök igen.',
        });

        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      }
    } catch (error) {
      console.error('Error:', error);

      setFeedback({
        type: 'error',
        message:
          'Serverfel, försök igen senare.',
      });

      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="contact-container"
      style={{
        backgroundColor: '#588b85',
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="contact-form"
        encType="multipart/form-data"
      >
        <h1>Boka Din Umrah Resa</h1>

        <p className="form-description">
          <strong>
            <em>
              OBS: Bokningen är inte säkrad
              förrän betalningen är gjord.
              <br />
              Vi kommer att skicka en
              betalningsförfrågan via e-post
              inom närmsta dagen som ni sedan
              betalar in för att säkra er plats.
            </em>
          </strong>

          <br />
          <br />

          Vi samlar in denna information för att
          kunna ansöka om visum och planera din
          resa på bästa möjliga sätt. Vänligen
          fyll i formuläret nedan med korrekta
          uppgifter. Vårt supportteam finns
          tillgängligt för att svara på frågor
          och hjälpa dig inför avresan, så att
          allt ska gå så smidigt som möjligt.

          <br />
          <br />

          <strong>
            <em>
              Disclaimer: Vi reserverar oss vid
              felskrivning.
            </em>
          </strong>
        </p>

        {/* NAMN */}

        <div className="form-row">
          <div className="form-half">
            <label>
              <strong>Förnamn</strong>
            </label>

            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-half">
            <label>
              <strong>Efternamn</strong>
            </label>

            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* ADRESS */}

        <label>
          <strong>Adress</strong>
        </label>

        <input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          required
        />

        {/* POSTNUMMER + ORT */}

        <div className="form-row">
          <div className="form-half">
            <label>
              <strong>Postnummer</strong>
            </label>

            <input
              type="text"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-half">
            <label>
              <strong>Ort</strong>
            </label>

            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* EMAIL + TELEFON */}

        <div className="form-row">
          <div className="form-half">
            <label>
              <strong>E-post</strong>
            </label>

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-half">
            <label>
              <strong>Mobilnummer</strong>
            </label>

            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* CIVILSTÅND + YRKE */}

        <div className="form-row">
          <div className="form-half">
            <label>
              <strong>Civilstånd</strong>
            </label>

            <select
              name="civilStatus"
              value={formData.civilStatus}
              onChange={handleChange}
              required
            >
              <option value="">
                Välj
              </option>

              <option value="ogift">
                Ogift
              </option>

              <option value="änka">
                Änka
              </option>

              <option value="gift">
                Gift
              </option>
            </select>
          </div>

          <div className="form-half">
            <label>
              <strong>Yrkesroll</strong>
            </label>

            <input
              type="text"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* =====================================================
            RESEPAKET
        ====================================================== */}

        <label>
          <strong>När vill du åka?</strong>
        </label>

        <select
          name="travelDate"
          value={formData.travelDate}
          onChange={handleChange}
          required
        >
          <option value="">
            Välj ett alternativ
          </option>

          {Object.entries(PACKAGE_CONFIG).map(
            ([id, pkg]) => (
              <option
                key={id}
                value={id}
              >
                {pkg.name}: {pkg.dates}
              </option>
            )
          )}
        </select>

        {/* =====================================================
            STANDARD / PREMIUM
        ====================================================== */}

        <label>
          <strong>Vilket paket?</strong>
        </label>

        <select
          name="package"
          value={formData.package}
          onChange={handleChange}
          required
          disabled={!formData.travelDate}
        >
          <option value="">
            Välj ett paket
          </option>

          <option value="standard">
            Standard Paket
            {standardFromPrice > 0
              ? ` (från ${formatPrice(
                  standardFromPrice
                )} kr)`
              : ''}
          </option>

          <option value="premium">
            Premium Paket
          </option>
        </select>

        {/* =====================================================
            PREMIUM RUM
        ====================================================== */}

        {formData.package === 'premium' && (
          <div>
            <label>
              <strong>Rumstyp</strong>
            </label>

            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="roomType"
                  value="fyrbäddsrum"
                  checked={
                    formData.roomType ===
                    'fyrbäddsrum'
                  }
                  onChange={handleChange}
                  required
                />

                Fyrbäddsrum
                {premiumRoomPrices.four > 0 &&
                  ` (från ${formatPrice(
                    premiumRoomPrices.four
                  )} kr)`}
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="roomType"
                  value="trebäddsrum"
                  checked={
                    formData.roomType ===
                    'trebäddsrum'
                  }
                  onChange={handleChange}
                />

                Trebäddsrum
                {premiumRoomPrices.three >
                  0 &&
                  ` (från ${formatPrice(
                    premiumRoomPrices.three
                  )} kr)`}
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="roomType"
                  value="tvåbäddsrum"
                  checked={
                    formData.roomType ===
                    'tvåbäddsrum'
                  }
                  onChange={handleChange}
                />

                Tvåbäddsrum
                {premiumRoomPrices.two > 0 &&
                  ` (från ${formatPrice(
                    premiumRoomPrices.two
                  )} kr)`}
              </label>
            </div>
          </div>
        )}

        {/* =====================================================
            HÄLSOFRÅGOR
        ====================================================== */}

        <label>
          <strong>
            Är du just nu inlagd på sjukhus?
          </strong>
        </label>

        <select
          name="q1"
          value={formData.q1}
          onChange={handleChange}
          required
        >
          <option value="">
            Välj
          </option>

          <option value="ja">
            Ja
          </option>

          <option value="nej">
            Nej
          </option>
        </select>

        <label>
          <strong>
            Har du varit med om en olycka?
          </strong>
        </label>

        <select
          name="q2"
          value={formData.q2}
          onChange={handleChange}
          required
        >
          <option value="">
            Välj
          </option>

          <option value="ja">
            Ja
          </option>

          <option value="nej">
            Nej
          </option>
        </select>

        <label>
          <strong>
            Har du några medfödda sjukdomar?
          </strong>
        </label>

        <select
          name="q3"
          value={formData.q3}
          onChange={handleChange}
          required
        >
          <option value="">
            Välj
          </option>

          <option value="ja">
            Ja
          </option>

          <option value="nej">
            Nej
          </option>
        </select>

        {/* =====================================================
            FOTO
        ====================================================== */}

        <label>
          <strong>Ansiktsfoto</strong>
        </label>

        <input
          type="file"
          name="facePhoto"
          onChange={handleFileChange}
          accept="image/*"
          required
          className="file-upload-button"
        />

        <div
          style={{
            fontWeight: 'bold',
            color: '#000000ff',
            fontSize: '0.95rem',
            marginLeft: '4px',
          }}
        >
          OBS: Ladda upp en tydlig bild på
          ditt ansikte utan glasögon eller
          huvudbonad.
        </div>

        {/* =====================================================
            PASS
        ====================================================== */}

        <label>
          <strong>Passkopia</strong>
        </label>

        <input
          type="file"
          name="passportCopy"
          onChange={handleFileChange}
          accept="image/*"
          required
          className="file-upload-button"
        />

        <div
          style={{
            fontWeight: 'bold',
            color: '#000000ff',
            fontSize: '0.95rem',
            marginLeft: '4px',
          }}
        >
          OBS: Passet ska vara giltigt minst
          6 månader och hela infosidan ska
          vara tydlig och synlig.
        </div>

        {/* =====================================================
            VILLKOR
        ====================================================== */}

        <div className="checkbox-line">
          <input
            type="checkbox"
            id="termsAccepted"
            name="termsAccepted"
            checked={
              formData.termsAccepted
            }
            onChange={handleChange}
            required
          />

          <label htmlFor="termsAccepted">
            Jag godkänner{' '}
            <span
              className="link clickable"
              onClick={() =>
                setShowTerms(true)
              }
            >
              Användarvillkoren
            </span>
          </label>
        </div>

        {/* =====================================================
            INTEGRITET
        ====================================================== */}

        <div className="checkbox-line">
          <input
            type="checkbox"
            id="privacyAccepted"
            name="privacyAccepted"
            checked={
              formData.privacyAccepted
            }
            onChange={handleChange}
            required
          />

          <label htmlFor="privacyAccepted">
            Jag godkänner{' '}
            <span
              className="link clickable"
              onClick={() =>
                setShowPrivacy(true)
              }
            >
              Integritetspolicyn
            </span>
          </label>
        </div>

        {/* =====================================================
            RECAPTCHA
        ====================================================== */}

        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey="6Lctpf4qAAAAAE_p4tR5efpFV25a1UHxsJjsdSmF"
          onChange={(token) =>
            setRecaptchaToken(token)
          }
        />

        {/* =====================================================
            SKICKA
        ====================================================== */}

        <button
          type="submit"
          disabled={isSubmitting}
          className="submit-button"
        >
          {isSubmitting
            ? 'Skickar... vänligen vänta'
            : 'Skicka Ansökan'}
        </button>

        {/* =====================================================
            FEEDBACK
        ====================================================== */}

        {feedback.message && (
          <div
            className={`mt-6 p-4 rounded text-white text-center ${
              feedback.type === 'success'
                ? 'bg-green-600'
                : 'bg-red-600'
            }`}
          >
            {feedback.message}
          </div>
        )}

        {/* =====================================================
            ANVÄNDARVILLKOR MODAL
        ====================================================== */}

        {showTerms && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button
                type="button"
                className="modal-close"
                onClick={() =>
                  setShowTerms(false)
                }
              >
                ×
              </button>

              <h2>Användarvillkor</h2>

              <p>
                Genom att använda detta
                formulär godkänner du att all
                information som lämnas är
                sanningsenlig och korrekt. Du
                samtycker till att vi behandlar
                dina uppgifter för att kunna
                fullfölja din bokning, erbjuda
                support och säkerställa
                visumprocessens genomförande.
              </p>

              <button
                type="button"
                onClick={() =>
                  setShowTerms(false)
                }
              >
                Stäng
              </button>
            </div>
          </div>
        )}

        {/* =====================================================
            INTEGRITETSPOLICY MODAL
        ====================================================== */}

        {showPrivacy && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button
                type="button"
                className="modal-close"
                onClick={() =>
                  setShowPrivacy(false)
                }
              >
                ×
              </button>

              <h2>Integritetspolicy</h2>

              <p>
                Vi samlar endast in information
                som är nödvändig för att
                slutföra din resa och
                visumansökan. Dina uppgifter
                behandlas konfidentiellt i
                enlighet med GDPR och delas
                inte med tredje part utan ditt
                samtycke.
              </p>

              <button
                type="button"
                onClick={() =>
                  setShowPrivacy(false)
                }
              >
                Stäng
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default Contact;